import { Button } from '@/components/ui/button';
import { ContentManagementMenu } from '@/components/content-management-menu';
import { Input } from '@/components/ui/input';
import { ContentEditingShell } from '@/components/content-editing-shell';
import { MediaLibrary, type CaseMediaSelection } from '@/components/media-library';
import { api } from '@/lib/api';
import { localizeContentImages } from '@/lib/localize-content-images';
import { ArrowLeft, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  translationBlocks,
  validTranslationCopy,
  type ContentLanguage,
  type FieldTranslationMetadata,
  type LocalizedText,
  type TranslationCopy,
} from '@fame/shared';
import { getLocalizedText } from '@/lib/utils';

type Article = {
  id: string;
  slug: string;
  title: Record<string, string>;
  excerpt?: Record<string, string>;
  content?: any;
  status: 'draft' | 'in_review' | 'changes_requested' | 'published';
  media: CaseMediaSelection[];
};
const empty: { nl?: string; fr?: string; en?: string } = { nl: '', fr: '', en: '' };
const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export function BlogArticlesPage() {
  const [articles, setArticles] = useState<Article[]>();
  useEffect(() => {
    void api.get<Article[]>('/blog').then(setArticles);
  }, []);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Blog</h1>
          <p className="mt-1 text-muted-foreground">Beheer inzichten en avonturen voor de website.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/blog/nieuw">
              <Plus className="mr-2 h-4 w-4" />
              Nieuw artikel
            </Link>
          </Button>
        </div>
      </div>
      <div className="rounded-md border">
        {articles?.map(article => (
          <Link
            className="flex items-center justify-between border-b p-4 last:border-0 hover:bg-muted/50"
            key={article.id}
            to={`/blog/${article.id}/bewerken`}
          >
            <div>
              <p className="font-medium">{getLocalizedText(article.title)}</p>
              <p className="text-sm text-muted-foreground">/blog/{article.slug}</p>
            </div>
            <span className="text-sm text-muted-foreground">{article.status}</span>
          </Link>
        ))}
        {articles?.length === 0 && (
          <p className="p-8 text-center text-muted-foreground">Nog geen artikelen aangemaakt.</p>
        )}
      </div>
    </div>
  );
}

export function BlogArticleEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article>();
  const [title, setTitle] = useState<LocalizedText>(empty);
  const [excerpt, setExcerpt] = useState<LocalizedText>(empty);
  const [sourceLanguage, setSourceLanguage] = useState<ContentLanguage>('nl');
  const [fieldMetadata, setFieldMetadata] = useState<FieldTranslationMetadata>({});
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState<any>({ type: 'doc', content: [] });
  const [translations, setTranslations] = useState<Partial<Record<ContentLanguage, TranslationCopy>>>({});
  const [media, setMedia] = useState<CaseMediaSelection[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!id) return;
    void api
      .get<Article>(`/blog/${id}`)
      .then(value => {
        setArticle(value);
        setTitle({ ...empty, ...value.title });
        setExcerpt({ ...empty, ...value.excerpt });
        setSlug(value.slug);
        const master = value.content?.master ?? { type: 'doc', content: [] };
        setContent(master);
        setSourceLanguage(value.content?.sourceLanguage ?? 'nl');
        setFieldMetadata(value.content?.fieldMetadata ?? {});
        setTranslations(
          Object.fromEntries(
            Object.entries(value.content?.translations ?? {}).map(([language, copy]) => [
              language,
              Object.fromEntries(
                Object.entries((copy ?? {}) as TranslationCopy).filter(([blockId, entry]) =>
                  validTranslationCopy(master, { [blockId]: entry })
                )
              ),
            ])
          ) as Partial<Record<ContentLanguage, TranslationCopy>>
        );
        setMedia(value.media.filter(item => item.role === 'hero' || item.role === 'gallery'));
      })
      .catch(() => toast.error('Artikel niet gevonden.'));
  }, [id]);
  const save = async (status: Article['status']) => {
    if (!title[sourceLanguage]?.trim() || !slugify(slug))
      return toast.error('Een titel in de originele taal en slug zijn verplicht.');
    setSaving(true);
    try {
      const masterBlockTypes = new Map(translationBlocks(content).map(block => [block.id, block.block.type]));
      const prunedTranslations = Object.fromEntries(
        Object.entries(translations).map(([language, copy]) => [
          language,
          Object.fromEntries(
            Object.entries(copy ?? {}).filter(([id, entry]) => entry && masterBlockTypes.get(id) === entry.block.type)
          ),
        ])
      ) as Partial<Record<ContentLanguage, TranslationCopy>>;
      const localizedContent = await localizeContentImages({ master: content, translations: prunedTranslations });
      setContent(localizedContent.master);
      setTranslations(localizedContent.translations);
      const data = {
        slug: slugify(slug),
        title,
        excerpt: Object.values(excerpt).some(Boolean) ? excerpt : undefined,
        content: {
          master: localizedContent.master,
          sourceLanguage,
          translations: localizedContent.translations,
          fieldMetadata,
        },
        status,
        media,
      };
      if (isNew) await api.post('/blog', data);
      else await api.put(`/blog/${id}`, data);
      toast.success(isNew ? 'Artikel aangemaakt.' : 'Artikel opgeslagen.');
      navigate('/blog');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Opslaan is niet gelukt.');
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/blog/${id}`);
      toast.success('Artikel verwijderd.');
      navigate('/blog');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Verwijderen is niet gelukt.');
      setDeleting(false);
    }
  };
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/blog">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{isNew ? 'Nieuw blogartikel' : 'Blogartikel bewerken'}</h1>
          <p className="text-muted-foreground">Schrijf het origineel en werk daarna per taal aan de vertalingen.</p>
        </div>
      </div>
      {(isNew || article?.id === id) && (
        <ContentEditingShell
          key={id ?? 'new-blog'}
          kind="blog"
          isNew={isNew}
          sourceLanguage={sourceLanguage}
          onSourceLanguageChange={setSourceLanguage}
          content={content}
          onContentChange={setContent}
          translations={translations}
          onTranslationsChange={setTranslations}
          fieldMetadata={fieldMetadata}
          onFieldMetadataChange={setFieldMetadata}
          fields={[
            {
              key: 'field:title',
              label: 'Titel',
              value: title,
              setValue: next => {
                setTitle(next);
                if (isNew && next[sourceLanguage] !== title[sourceLanguage])
                  setSlug(slugify(next[sourceLanguage] ?? ''));
              },
            },
            { key: 'field:excerpt', label: 'Korte intro', value: excerpt, setValue: setExcerpt, multiline: true },
          ]}
          draft={{
            slug: slugify(slug),
            title,
            excerpt: Object.values(excerpt).some(Boolean) ? excerpt : undefined,
            content: { master: content, sourceLanguage, translations, fieldMetadata },
            status: article?.status ?? 'draft',
            media,
          }}
          guardDisabled={saving || deleting}
          settings={
            <>
              <label className="block space-y-2">
                <span className="font-medium">Slug</span>
                <Input value={slug} onChange={event => setSlug(event.target.value)} />
              </label>
              <MediaLibrary value={media} onChange={setMedia} variant="blog" />
            </>
          }
          actions={
            <>
              <Button disabled={saving || deleting} onClick={() => save(article?.status ?? 'draft')}>
                {saving ? 'Opslaan…' : 'Opslaan'}
              </Button>
              {(!article || article.status === 'draft' || article.status === 'changes_requested') && (
                <Button variant="outline" disabled={saving || deleting} onClick={() => save('in_review')}>
                  Ter review
                </Button>
              )}
              {article?.status === 'in_review' && (
                <Button variant="outline" disabled={saving || deleting} onClick={() => save('published')}>
                  Publiceren
                </Button>
              )}
              {!isNew && (
                <ContentManagementMenu
                  kind="blog"
                  disabled={saving || deleting}
                  onRemove={remove}
                  onWithdraw={
                    article?.status === 'published'
                      ? () => {
                          void save('draft');
                        }
                      : undefined
                  }
                />
              )}
            </>
          }
        />
      )}
    </div>
  );
}
